if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, scrollToImage = true) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!scrollToImage) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

if (!customElements.get('gallery-dots')) {
  customElements.define(
    'gallery-dots',
    class GalleryDots extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.sliderComponent = document.getElementById(this.dataset.slider);
        this.slider = this.sliderComponent && this.sliderComponent.querySelector('[id^="Slider-"]');
        if (!this.slider) return;
        this.initialized = true;

        let rebuildTimer;
        this.scheduleBuild = () => {
          clearTimeout(rebuildTimer);
          rebuildTimer = setTimeout(() => this.build(), 80);
        };

        this.onScroll = () => {
          if (this.scrollRaf) return;
          this.scrollRaf = requestAnimationFrame(() => {
            this.scrollRaf = null;
            this.setActiveFromScroll();
          });
        };
        this.slider.addEventListener('scroll', this.onScroll);

        // Build once the slider has real dimensions, and on viewport resize.
        this.resizeObserver = new ResizeObserver(this.scheduleBuild);
        this.resizeObserver.observe(this.slider);

        // Rebuild when slides are shown/hidden (e.g. variant colour filtering).
        this.mutationObserver = new MutationObserver(this.scheduleBuild);
        this.mutationObserver.observe(this.slider, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class'],
        });

        this.build();
      }

      visibleSlides() {
        return Array.from(this.slider.querySelectorAll('[id^="Slide-"]')).filter((slide) => slide.clientWidth > 0);
      }

      build() {
        this.slides = this.visibleSlides();
        this.innerHTML = '';
        if (this.slides.length < 2) {
          this.setAttribute('hidden', '');
          return;
        }
        this.removeAttribute('hidden');

        this.dots = this.slides.map((slide, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'gallery-dots__dot';
          dot.setAttribute('aria-label', `Go to image ${index + 1}`);
          dot.addEventListener('click', () => {
            this.slider.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
          });
          this.appendChild(dot);
          return dot;
        });

        this.setActiveFromScroll();
      }

      setActiveFromScroll() {
        if (!this.dots || !this.slides.length) return;
        const center = this.slider.scrollLeft + this.slider.clientWidth / 2;
        let activeIndex = 0;
        let minDistance = Infinity;
        this.slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft + slide.clientWidth / 2 - center);
          if (distance < minDistance) {
            minDistance = distance;
            activeIndex = index;
          }
        });
        this.dots.forEach((dot, index) => dot.classList.toggle('is-active', index === activeIndex));
      }
    }
  );
}
